
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // You can also log the error to an error reporting service
    console.error(`Error in ${this.props.componentName || 'component'}:`, error);
    console.error("Component stack:", errorInfo.componentStack);
    
    this.setState({
      error,
      errorInfo
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <Card className="border-red-300 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-600">Something went wrong in {this.props.componentName || 'this component'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <h3 className="font-semibold">Error:</h3>
              <pre className="bg-red-100 p-3 rounded-md overflow-auto max-h-[200px] text-sm">
                {this.state.error?.toString()}
              </pre>
            </div>
            
            {this.state.errorInfo && (
              <div>
                <h3 className="font-semibold">Component Stack:</h3>
                <pre className="bg-red-100 p-3 rounded-md overflow-auto max-h-[300px] text-sm">
                  {this.state.errorInfo.componentStack}
                </pre>
              </div>
            )}
            
            <Button 
              className="mt-4" 
              variant="destructive"
              onClick={() => {
                window.location.href = '/';
              }}
            >
              Go to Home Page
            </Button>
            <Button 
              className="mt-4 ml-2" 
              variant="outline"
              onClick={() => {
                this.setState({ hasError: false, error: null, errorInfo: null });
              }}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
